import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../common/ConfirmDialog';
import FormStepper from '../common/FormStepper';
import LanguageFlag from '../common/LanguageFlag';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SalovareSelectField from '../common/SalovareSelectField';
import ScrollableDialog from '../common/ScrollableDialog';
import SwitchField from '../common/SwitchField';
import ServicePrepaymentFields from './ServicePrepaymentFields';
import ConsentFormSelectField from '../consent-forms/ConsentFormSelectField';
import ProLockedSection from '../billing/ProLockedSection';
import { buildAdornmentTextFieldSx, fieldLabelRowSx } from '../common/formFieldStyles';
import { SUPPORTED_LANGUAGE_CODES } from '../../constants/Languages';
import { useAppData } from '../../context/AppDataContext';
import useStorePlan from '../../hooks/useStorePlan';
import { DepositType, PrepaymentMode, normalizeDepositType, normalizePrepaymentMode } from '../../constants/PrepaymentMode';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { validateServicePrepayment } from '../../utils/prepaymentUtils';
import { readVatClassification, validateVatClassification } from '../../utils/vatClassification';
import ServiceProductsUsedField from './ServiceProductsUsedField';
import VatClassificationFields from '../common/VatClassificationFields';
import ServiceService from '../../services/serviceService';
import { isFreeServicePrice, normalizeFreeServiceUsageLimit } from '../../utils/freeServiceUtils';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { BRAND } from '../../theme/brandTokens';
import {
  ServiceValueMode,
  buildDurationMinuteOptions,
  isSchedulableDuration,
  normalizeServiceValueMode,
} from '../../utils/serviceValueFormat';

const DEFAULT_DURATION = 30;
const DEFAULT_TYPE = 'Primary';
const STEP_DETAILS = 0;
const STEP_PRICING = 1;
const STEP_CONSENT = 2;
const LAST_STEP = STEP_CONSENT;

const isPrimaryService = (type) => type === DEFAULT_TYPE;

const buildLocalizedFields = (supportedLanguages, values = {}) => (
  Object.fromEntries(supportedLanguages.map((language) => [language, values?.[language] || '']))
);

const buildInitialService = (supportedLanguages, service) => ({
  id: service?.id ?? null,
  name: buildLocalizedFields(supportedLanguages, service?.name),
  description: buildLocalizedFields(supportedLanguages, service?.description),
  durationMode: service?.includedInPlanOrPackage
    ? ServiceValueMode.Fixed
    : normalizeServiceValueMode(service?.durationMode),
  duration: service?.duration || DEFAULT_DURATION,
  durationMax: service?.durationMax || '',
  priceMode: service?.includedInPlanOrPackage
    ? ServiceValueMode.Fixed
    : normalizeServiceValueMode(service?.priceMode),
  price: service?.price ?? '',
  priceMax: service?.priceMax ?? '',
  includedInPlanOrPackage: Boolean(service?.includedInPlanOrPackage),
  usageLimit: service?.usageLimit ?? '',
  visibleToCustomers: service?.visibleToCustomers !== false,
  type: service?.type || DEFAULT_TYPE,
  isStandalone: service?.type === 'Secondary' ? Boolean(service?.isStandalone) : true,
  disabled: Boolean(service?.disabled),
  prepaymentMode: normalizePrepaymentMode(service?.prepaymentMode),
  depositType: service?.depositType ? normalizeDepositType(service.depositType) : DepositType.FixedAmount,
  depositValue: service?.depositValue ?? '',
  allowInstallments: service?.allowInstallments === true,
  teamMemberIds: Array.isArray(service?.teamMemberIds) ? [...service.teamMemberIds] : [],
  consentFormId: service?.consentFormId ?? null,
  ...readVatClassification(service),
});

const buildTextFieldSx = (hasError = false, multiline = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  lineHeight: multiline ? 1.5 : 'normal',
  resize: multiline ? 'vertical' : 'none',
  minHeight: multiline ? 112 : 48,
  '&:focus': {
    borderColor: BRAND.accent,
  },
});

const typeHelpIconButtonSx = {
  p: 0,
  width: 20,
  height: 20,
  minWidth: 20,
  minHeight: 20,
  color: '#8a8a8a',
  '&:hover': {
    color: BRAND.accentText,
    backgroundColor: 'transparent',
  },
};

const stepForErrorKey = (key) => {
  if (key === 'name' || key === 'duration') {
    return STEP_DETAILS;
  }

  return STEP_PRICING;
};

const firstStepWithError = (errors) => (
  Math.min(...Object.keys(errors).map(stepForErrorKey))
);

const collectErrors = ({ formData, supportedLanguages, isPro, step }) => {
  const nextErrors = {};
  const includeDetails = step == null || step === STEP_DETAILS;
  const includePricing = step == null || step === STEP_PRICING;
  const priceMode = normalizeServiceValueMode(formData.priceMode);
  const durationMode = normalizeServiceValueMode(formData.durationMode);
  const parsedPrice = Number(formData.price);
  const parsedPriceMax = Number(formData.priceMax);
  const parsedDuration = Number(formData.duration);
  const parsedDurationMax = Number(formData.durationMax);
  const fixedPrice = priceMode === ServiceValueMode.Fixed;

  if (includeDetails) {
    const hasAllNames = supportedLanguages.every((language) => formData.name?.[language]?.trim());
    if (!hasAllNames) {
      nextErrors.name = 'servicesManagement.validation.nameRequired';
    }

    if (durationMode === ServiceValueMode.Fixed && (!Number.isFinite(parsedDuration) || parsedDuration <= 0)) {
      nextErrors.duration = 'servicesManagement.validation.durationRequired';
    }

    if (durationMode === ServiceValueMode.Range) {
      if (!isSchedulableDuration(parsedDuration) || !isSchedulableDuration(parsedDurationMax) || parsedDurationMax <= parsedDuration) {
        nextErrors.duration = 'servicesManagement.validation.durationRange';
      }
    }
  }

  if (includePricing) {
    if (fixedPrice && (formData.price === '' || !Number.isFinite(parsedPrice) || parsedPrice < 0)) {
      nextErrors.price = 'servicesManagement.validation.priceRequired';
    }

    if (priceMode === ServiceValueMode.Range) {
      const maxProvided = formData.priceMax !== '' && formData.priceMax != null;
      if (formData.price === '' || !Number.isFinite(parsedPrice) || parsedPrice < 0) {
        nextErrors.price = 'servicesManagement.validation.priceRangeMin';
      } else if (maxProvided && (!Number.isFinite(parsedPriceMax) || parsedPriceMax <= parsedPrice)) {
        nextErrors.price = 'servicesManagement.validation.priceRange';
      }
    }

    if (fixedPrice && isFreeServicePrice(formData.price) && formData.usageLimit !== '' && formData.usageLimit != null) {
      const usageLimit = Number(formData.usageLimit);
      if (!Number.isInteger(usageLimit) || usageLimit < 1) {
        nextErrors.usageLimit = 'servicesManagement.validation.usageLimitInvalid';
      }
    }

    const prepaymentError = isPro && fixedPrice && !isFreeServicePrice(formData.price)
      ? validateServicePrepayment({ ...formData, price: parsedPrice })
      : null;
    if (prepaymentError) {
      nextErrors.prepayment = prepaymentError;
    }

    Object.assign(nextErrors, validateVatClassification(formData));
  }

  return nextErrors;
};

const ServiceEditorDialog = ({
  open,
  mode,
  service,
  teamMembers = [],
  supportedLanguages,
  currency,
  saving,
  onClose,
  onSave,
  catalogProducts,
  canSeeCosts = false,
  onlinePaymentsReady = false,
  installmentMethodsAvailable = false,
  consentForms = [],
  consentFormsEnabled = false,
}) => {
  const { t } = useTranslation();
  const { currentStore } = useAppData();
  const { isPro } = useStorePlan(currentStore?.id, Boolean(currentStore?.id));
  const soleProfessional = teamMembers.length === 1 ? teamMembers[0] : null;
  const hideTeamAssignment = !isPro || Boolean(soleProfessional);
  const [formData, setFormData] = useState(() => buildInitialService(supportedLanguages, service));
  const showRecipe = Array.isArray(catalogProducts);
  const [recipeLines, setRecipeLines] = useState([]);
  const [recipeReady, setRecipeReady] = useState(!showRecipe);
  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(STEP_DETAILS);
  const contentRef = useRef(null);
  const [unassignedWarningOpen, setUnassignedWarningOpen] = useState(false);
  const [typeHelpOpen, setTypeHelpOpen] = useState(false);
  const sortedLanguages = useMemo(
    () => [...supportedLanguages].sort(
      (a, b) => SUPPORTED_LANGUAGE_CODES.indexOf(a) - SUPPORTED_LANGUAGE_CODES.indexOf(b)
    ),
    [supportedLanguages]
  );
  const [activeLanguage, setActiveLanguage] = useState(() => supportedLanguages[0] || 'en');
  const steps = useMemo(() => ([
    { id: 'details', label: t('servicesManagement.dialog.steps.details') },
    { id: 'pricing', label: t('servicesManagement.dialog.steps.pricing') },
    { id: 'consent', label: t('servicesManagement.dialog.steps.consent') },
  ]), [t]);
  const isLastStep = activeStep === LAST_STEP;

  useEffect(() => {
    if (open) {
      setFormData(buildInitialService(supportedLanguages, service));
      setErrors({});
      setActiveStep(STEP_DETAILS);
      setUnassignedWarningOpen(false);
      setTypeHelpOpen(false);
    }
  }, [open, service, supportedLanguages]);

  useEffect(() => {
    if (!open || !showRecipe) {
      setRecipeLines([]);
      setRecipeReady(true);
      return undefined;
    }

    if (!service?.id || !currentStore?.id) {
      setRecipeLines([]);
      setRecipeReady(true);
      return undefined;
    }

    let cancelled = false;
    setRecipeReady(false);
    requestDeduplicator.deduplicate(
      `service-recipe-${currentStore.id}-${service.id}`,
      () => ServiceService.getServiceRecipe(currentStore.id, service.id),
    ).then((lines) => {
      if (!cancelled) {
        setRecipeLines(lines || []);
        setRecipeReady(true);
      }
    }).catch(() => {
      if (!cancelled) {
        setRecipeLines([]);
        setRecipeReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [open, showRecipe, service?.id, currentStore?.id]);

  useEffect(() => {
    if (!open || !contentRef.current) {
      return;
    }

    contentRef.current.scrollTop = 0;
  }, [activeStep, open]);

  useEffect(() => {
    if (!open || sortedLanguages.length === 0) {
      return;
    }

    setActiveLanguage(sortedLanguages[0]);
  }, [open, sortedLanguages]);

  useEffect(() => {
    if (sortedLanguages.length === 0 || sortedLanguages.includes(activeLanguage)) {
      return;
    }

    setActiveLanguage(sortedLanguages[0]);
  }, [activeLanguage, sortedLanguages]);

  const durationOptions = useMemo(() => buildDurationMinuteOptions(), []);
  const modesLocked = Boolean(formData.includedInPlanOrPackage);
  const priceMode = normalizeServiceValueMode(formData.priceMode);
  const durationMode = normalizeServiceValueMode(formData.durationMode);
  const fixedPrice = priceMode === ServiceValueMode.Fixed;

  const toggleTeamMember = (teamMemberId) => {
    setFormData((previous) => {
      const isSelected = previous.teamMemberIds.some((id) => String(id) === String(teamMemberId));

      return {
        ...previous,
        teamMemberIds: isSelected
          ? previous.teamMemberIds.filter((id) => String(id) !== String(teamMemberId))
          : [...previous.teamMemberIds, teamMemberId],
      };
    });
  };

  const buildSavePayload = () => {
    const nextPriceMode = normalizeServiceValueMode(formData.priceMode);
    const free = nextPriceMode === ServiceValueMode.Fixed && isFreeServicePrice(formData.price);
    const clearsTerms = free || nextPriceMode !== ServiceValueMode.Fixed;
    return {
      ...formData,
      priceMode: nextPriceMode,
      durationMode: normalizeServiceValueMode(formData.durationMode),
      isStandalone: isPrimaryService(formData.type) ? true : Boolean(formData.isStandalone),
      price: nextPriceMode === ServiceValueMode.OnRequest ? 0 : Number(formData.price),
      priceMax: nextPriceMode === ServiceValueMode.Range && formData.priceMax !== '' && formData.priceMax != null
        ? Number(formData.priceMax)
        : null,
      usageLimit: nextPriceMode === ServiceValueMode.Fixed
        ? normalizeFreeServiceUsageLimit(formData.price, formData.usageLimit)
        : null,
      visibleToCustomers: formData.visibleToCustomers !== false,
      prepaymentMode: clearsTerms ? PrepaymentMode.NotRequired : formData.prepaymentMode,
      depositValue: clearsTerms ? '' : formData.depositValue,
      allowInstallments: clearsTerms ? false : formData.allowInstallments,
      duration: Number(formData.duration),
      durationMax: normalizeServiceValueMode(formData.durationMode) === ServiceValueMode.Range
        ? Number(formData.durationMax)
        : null,
      name: buildLocalizedFields(supportedLanguages, formData.name),
      description: buildLocalizedFields(supportedLanguages, formData.description),
      teamMemberIds: soleProfessional ? [soleProfessional.id] : formData.teamMemberIds,
      products: showRecipe ? recipeLines.map((line) => ({
        productId: line.productId,
        quantity: Number(line.quantity),
      })) : null,
    };
  };

  const handleNext = () => {
    const nextErrors = collectErrors({
      formData,
      supportedLanguages,
      isPro,
      step: activeStep,
    });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setActiveStep((previous) => Math.min(previous + 1, LAST_STEP));
  };

  const handleBack = () => {
    setActiveStep((previous) => Math.max(previous - 1, STEP_DETAILS));
  };

  const handleSubmit = () => {
    if (showRecipe && !recipeReady) {
      return;
    }

    const nextErrors = collectErrors({ formData, supportedLanguages, isPro });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setActiveStep(firstStepWithError(nextErrors));
      return;
    }

    if (!hideTeamAssignment && !formData.teamMemberIds.length) {
      setUnassignedWarningOpen(true);
      return;
    }

    onSave(buildSavePayload());
  };

  const handleConfirmUnassignedSave = () => {
    setUnassignedWarningOpen(false);
    onSave(buildSavePayload());
  };

  return (
    <>
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      title={mode === 'edit' ? t('servicesManagement.dialog.editTitle') : t('servicesManagement.dialog.createTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{
        flexShrink: 0,
        justifyContent: 'space-between',
      }}
      dialogContentProps={{ ref: contentRef }}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving} color="inherit">
            {t('common.cancel')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              id="service-editor-back"
              onClick={handleBack}
              disabled={saving || activeStep === STEP_DETAILS}
              color="inherit"
            >
              {t('common.back')}
            </Button>
            {isLastStep ? (
              <PrimaryActionButton
                id="service-editor-save"
                onClick={handleSubmit}
                disabled={saving}
              >
                {saving
                  ? t('common.saving')
                  : mode === 'edit'
                    ? t('servicesManagement.dialog.saveEdit')
                    : t('servicesManagement.dialog.saveCreate')}
              </PrimaryActionButton>
            ) : (
              <PrimaryActionButton
                id="service-editor-next"
                onClick={handleNext}
                disabled={saving}
              >
                {t('common.next')}
              </PrimaryActionButton>
            )}
          </Box>
        </>
      )}
    >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormStepper
            id="service-editor-stepper"
            steps={steps}
            activeStep={activeStep}
            onStepSelect={setActiveStep}
          />

          {modesLocked ? (
            <Typography variant="body2" color="text.secondary">
              {t('servicesManagement.planOrPackageLocked')}
            </Typography>
          ) : null}

          {activeStep === STEP_DETAILS ? (
            <>
          <Box
            sx={{
              border: '1px solid',
              borderColor: errors.name ? 'error.main' : 'divider',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={activeLanguage}
              onChange={(_, nextLanguage) => setActiveLanguage(nextLanguage)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                backgroundColor: '#f8f9fa',
                '& .MuiTab-root': {
                  color: '#666',
                  fontWeight: 600,
                  textTransform: 'none',
                  minHeight: 48,
                },
                '& .Mui-selected': {
                  color: BRAND.accentText,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: BRAND.accent,
                },
              }}
            >
              {sortedLanguages.map((language) => (
                <Tab
                  key={language}
                  value={language}
                  label={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                      <LanguageFlag languageCode={language} size="1rem" />
                      <Typography variant="body2">{t(`common.languages.${language}`)}</Typography>
                    </Box>
                  )}
                />
              ))}
            </Tabs>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, p: 2.5 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {`${t('servicesManagement.fields.name')} (${t(`common.languages.${activeLanguage}`)}) *`}
                </Typography>
                <Box
                  component="input"
                  type="text"
                  aria-label={`${t('servicesManagement.fields.name')} (${t(`common.languages.${activeLanguage}`)})`}
                  value={formData.name?.[activeLanguage] || ''}
                  onChange={(event) => setFormData((previous) => ({
                    ...previous,
                    name: {
                      ...previous.name,
                      [activeLanguage]: event.target.value,
                    },
                  }))}
                  sx={buildTextFieldSx(Boolean(errors.name))}
                />
                {errors.name ? (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                    {t(errors.name)}
                  </Typography>
                ) : null}
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {`${t('servicesManagement.fields.description')} (${t(`common.languages.${activeLanguage}`)})`}
                </Typography>
                <Box
                  component="textarea"
                  rows={4}
                  aria-label={`${t('servicesManagement.fields.description')} (${t(`common.languages.${activeLanguage}`)})`}
                  value={formData.description?.[activeLanguage] || ''}
                  onChange={(event) => setFormData((previous) => ({
                    ...previous,
                    description: {
                      ...previous.description,
                      [activeLanguage]: event.target.value,
                    },
                  }))}
                  sx={buildTextFieldSx(false, true)}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <SalovareSelectField
              label={t('servicesManagement.fields.type')}
              value={formData.type}
              onChange={(event) => setFormData((previous) => {
                const nextType = event.target.value;

                return {
                  ...previous,
                  type: nextType,
                  isStandalone: isPrimaryService(nextType) ? true : previous.isStandalone,
                };
              })}
              labelAdornment={(
                <IconButton
                  type="button"
                  size="small"
                  aria-label={t('servicesManagement.fields.typeHelpAriaLabel')}
                  onClick={() => setTypeHelpOpen(true)}
                  sx={typeHelpIconButtonSx}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            >
              <MenuItem value="Primary">{t('servicesManagement.types.primary')}</MenuItem>
              <MenuItem value="Secondary">{t('servicesManagement.types.secondary')}</MenuItem>
            </SalovareSelectField>

            <SalovareSelectField
              label={t('servicesManagement.fields.durationMode')}
              value={durationMode}
              disabled={modesLocked}
              onChange={(event) => setFormData((previous) => ({
                ...previous,
                durationMode: event.target.value,
              }))}
            >
              <MenuItem value={ServiceValueMode.Fixed}>{t('servicesManagement.valueMode.fixed')}</MenuItem>
              <MenuItem value={ServiceValueMode.Range}>{t('servicesManagement.valueMode.range')}</MenuItem>
              <MenuItem value={ServiceValueMode.OnRequest}>{t('servicesManagement.valueMode.onRequest')}</MenuItem>
            </SalovareSelectField>

            {durationMode !== ServiceValueMode.OnRequest ? (
            <SalovareSelectField
              label={t(durationMode === ServiceValueMode.Range
                ? 'servicesManagement.fields.durationMin'
                : 'servicesManagement.fields.duration')}
              required
              error={Boolean(errors.duration)}
              helperText={errors.duration ? t(errors.duration) : undefined}
              value={formData.duration}
              onChange={(event) => setFormData((previous) => ({ ...previous, duration: event.target.value }))}
            >
              {durationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </SalovareSelectField>
            ) : <Box />}

            {durationMode === ServiceValueMode.Range ? (
            <SalovareSelectField
              label={t('servicesManagement.fields.durationMax')}
              required
              error={Boolean(errors.duration)}
              value={formData.durationMax}
              onChange={(event) => setFormData((previous) => ({ ...previous, durationMax: event.target.value }))}
            >
              {durationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </SalovareSelectField>
            ) : null}

            {formData.type === 'Secondary' ? (
              <SwitchField
                containerSx={{ gridColumn: '1 / -1', pl: 0, pr: 1 }}
                checked={Boolean(formData.isStandalone)}
                onChange={(event) => setFormData((previous) => ({ ...previous, isStandalone: event.target.checked }))}
                label={t('servicesManagement.fields.isStandalone')}
                helpText={t('servicesManagement.fields.isStandaloneHelp')}
              />
            ) : null}

            <SwitchField
              containerSx={{ gridColumn: '1 / -1', pl: 0, pr: 1 }}
              checked={!formData.disabled}
              onChange={(event) => setFormData((previous) => ({ ...previous, disabled: !event.target.checked }))}
              label={t('servicesManagement.fields.isActive')}
              helpText={t('servicesManagement.fields.isActiveHelp')}
            />

            <SwitchField
              containerSx={{ gridColumn: '1 / -1', pl: 0, pr: 1 }}
              checked={formData.visibleToCustomers !== false}
              onChange={(event) => setFormData((previous) => ({ ...previous, visibleToCustomers: event.target.checked }))}
              label={t('servicesManagement.fields.visibleToCustomers')}
              helpText={t('servicesManagement.fields.visibleToCustomersHelp')}
            />
          </Box>

          {!hideTeamAssignment && (
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              p: 2.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              {t('servicesManagement.fields.teamMembers')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {t('servicesManagement.fields.teamMembersHelp')}
            </Typography>
            {teamMembers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('servicesManagement.fields.noTeamMembersAvailable')}
              </Typography>
            ) : (
              <FormGroup>
                {teamMembers.map((member) => (
                  <FormControlLabel
                    key={member.id}
                    control={(
                      <Checkbox
                        checked={formData.teamMemberIds.some((id) => String(id) === String(member.id))}
                        onChange={() => toggleTeamMember(member.id)}
                      />
                    )}
                    label={member.disabled
                      ? `${member.name || member.id} (${t('servicesManagement.status.inactive')})`
                      : (member.name || member.id)}
                  />
                ))}
              </FormGroup>
            )}
          </Box>
          )}
            </>
          ) : null}

          {activeStep === STEP_PRICING ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SalovareSelectField
              label={t('servicesManagement.fields.priceMode')}
              value={priceMode}
              disabled={modesLocked}
              onChange={(event) => {
                const nextMode = event.target.value;
                setFormData((previous) => ({
                  ...previous,
                  priceMode: nextMode,
                  ...(nextMode === ServiceValueMode.Fixed ? {} : {
                    usageLimit: '',
                    prepaymentMode: PrepaymentMode.NotRequired,
                    depositValue: '',
                    allowInstallments: false,
                  }),
                }));
              }}
            >
              <MenuItem value={ServiceValueMode.Fixed}>{t('servicesManagement.valueMode.fixed')}</MenuItem>
              <MenuItem value={ServiceValueMode.Range}>{t('servicesManagement.valueMode.range')}</MenuItem>
              <MenuItem value={ServiceValueMode.OnRequest}>{t('servicesManagement.valueMode.onRequest')}</MenuItem>
            </SalovareSelectField>

            {priceMode !== ServiceValueMode.OnRequest ? (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={fieldLabelRowSx}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {`${t(priceMode === ServiceValueMode.Range ? 'servicesManagement.fields.priceMin' : 'servicesManagement.fields.price')} *`}
                </Typography>
              </Box>
              <TextField
                type="number"
                value={formData.price}
                onChange={(event) => {
                  const price = event.target.value;
                  setFormData((previous) => ({
                    ...previous,
                    price,
                    usageLimit: isFreeServicePrice(price) ? previous.usageLimit : '',
                    ...(isFreeServicePrice(price)
                      ? {
                        prepaymentMode: PrepaymentMode.NotRequired,
                        depositValue: '',
                        allowInstallments: false,
                      }
                      : {}),
                  }));
                }}
                inputProps={{
                  min: 0,
                  step: 0.01,
                  'aria-label': t('servicesManagement.fields.price'),
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {getCurrencySymbol(currency)}
                    </InputAdornment>
                  ),
                }}
                error={Boolean(errors.price)}
                sx={buildAdornmentTextFieldSx(Boolean(errors.price))}
                fullWidth
              />
              {errors.price ? (
                <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                  {t(errors.price)}
                </Typography>
              ) : priceMode === ServiceValueMode.Fixed ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('servicesManagement.fields.priceHelp')}
                </Typography>
              ) : null}
            </Box>
            ) : null}

            {priceMode === ServiceValueMode.Range ? (
              <Box sx={{ minWidth: 0 }}>
                <Box sx={fieldLabelRowSx}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {t('servicesManagement.fields.priceMax')}
                  </Typography>
                </Box>
                <TextField
                  type="number"
                  value={formData.priceMax}
                  onChange={(event) => setFormData((previous) => ({ ...previous, priceMax: event.target.value }))}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                    'aria-label': t('servicesManagement.fields.priceMax'),
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {getCurrencySymbol(currency)}
                      </InputAdornment>
                    ),
                  }}
                  error={Boolean(errors.price)}
                  sx={buildAdornmentTextFieldSx(Boolean(errors.price))}
                  fullWidth
                />
              </Box>
            ) : null}

            {fixedPrice && isFreeServicePrice(formData.price) ? (
              <Box sx={{ minWidth: 0 }}>
                <Box sx={fieldLabelRowSx}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {t('servicesManagement.fields.usageLimit')}
                  </Typography>
                </Box>
                <TextField
                  type="number"
                  value={formData.usageLimit}
                  onChange={(event) => setFormData((previous) => ({ ...previous, usageLimit: event.target.value }))}
                  inputProps={{
                    min: 1,
                    step: 1,
                    'aria-label': t('servicesManagement.fields.usageLimit'),
                  }}
                  error={Boolean(errors.usageLimit)}
                  sx={buildAdornmentTextFieldSx(Boolean(errors.usageLimit))}
                  fullWidth
                />
                {errors.usageLimit ? (
                  <Typography variant="caption" sx={{ color: '#f44336', mt: 0.5, display: 'block' }}>
                    {t(errors.usageLimit)}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {t('servicesManagement.fields.usageLimitHelp')}
                  </Typography>
                )}
              </Box>
            ) : null}

            {fixedPrice && !isFreeServicePrice(formData.price) ? (
            <ProLockedSection locked={!isPro}>
              <ServicePrepaymentFields
                value={{
                  prepaymentMode: formData.prepaymentMode,
                  depositType: formData.depositType,
                  depositValue: formData.depositValue,
                }}
                price={formData.price}
                currency={currency}
                error={errors.prepayment}
                onlinePaymentsReady={onlinePaymentsReady}
                storeId={currentStore?.id}
                onChange={(prepayment) => setFormData((previous) => ({ ...previous, ...prepayment }))}
              />
            </ProLockedSection>
            ) : null}

            {fixedPrice && !isFreeServicePrice(formData.price) && ((isPro && onlinePaymentsReady && installmentMethodsAvailable) || !isPro) ? (
              <ProLockedSection locked={!isPro}>
                <SwitchField
                  containerSx={{ pl: 0, pr: 1 }}
                  checked={formData.allowInstallments === true}
                  onChange={(event) => setFormData((previous) => ({ ...previous, allowInstallments: event.target.checked }))}
                  label={t('servicesManagement.prepayment.allowInstallments')}
                />
              </ProLockedSection>
            ) : null}

            <VatClassificationFields
              idPrefix="service-vat"
              value={formData}
              errors={errors}
              onChange={(next) => setFormData((previous) => ({ ...previous, ...next }))}
            />

            {showRecipe ? (
              <ServiceProductsUsedField
                products={catalogProducts}
                lines={recipeLines}
                onChange={setRecipeLines}
                servicePrice={formData.price}
                currency={currency}
                canSeeCosts={canSeeCosts}
              />
            ) : null}
            </Box>
          ) : null}

          {activeStep === STEP_CONSENT ? (
            <ProLockedSection locked={!consentFormsEnabled}>
              <ConsentFormSelectField
                id="service-consent-form"
                value={formData.consentFormId}
                consentForms={consentForms}
                onChange={(consentFormId) => setFormData((previous) => ({ ...previous, consentFormId }))}
                disabled={saving || !consentFormsEnabled}
              />
            </ProLockedSection>
          ) : null}
        </Box>
    </ScrollableDialog>

      <ScrollableDialog
        open={typeHelpOpen}
        onClose={() => setTypeHelpOpen(false)}
        maxWidth="sm"
        showCloseButton
        dividers
        title={t('servicesManagement.fields.typeHelpTitle')}
        titleSx={{ pb: 1, flexShrink: 0 }}
        actionsSx={{ flexShrink: 0, justifyContent: 'flex-end' }}
        actions={(
          <PrimaryActionButton onClick={() => setTypeHelpOpen(false)}>
            {t('common.close')}
          </PrimaryActionButton>
        )}
      >
        <Stack spacing={1.5}>
          <Typography variant="body2">
            {t('servicesManagement.fields.typeHelpPrimary')}
          </Typography>
          <Typography variant="body2">
            {t('servicesManagement.fields.typeHelpSecondary')}
          </Typography>
          <Typography variant="body2">
            {t('servicesManagement.fields.typeHelpExample')}
          </Typography>
        </Stack>
      </ScrollableDialog>

      <ConfirmDialog
        open={unassignedWarningOpen}
        onClose={() => setUnassignedWarningOpen(false)}
        onConfirm={handleConfirmUnassignedSave}
        title={t('servicesManagement.unassignedWarning.title')}
        message={t('servicesManagement.unassignedWarning.message')}
        confirmText={mode === 'edit'
          ? t('servicesManagement.unassignedWarning.confirmSave')
          : t('servicesManagement.unassignedWarning.confirmCreate')}
        cancelText={t('common.cancel')}
        confirmColor="warning"
        loading={saving}
      />
    </>
  );
};

export default ServiceEditorDialog;
